import { useMutation, useQuery } from "@apollo/client";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Edit from "@mui/icons-material/Edit";
import Search from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { ExtractedAddress, FacilityMatchSearchQuery } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import {
  FormShapeWithAlternateIds,
  GovernmentIdFields,
} from "routes/Customer/Facility/ProfileV2/GovernmentIdFields";

import {
  FACILITY_ALTERNATE_IDS_QUERY,
  FACILITY_SEARCH_QUERY,
  MARK_FACILITY_NOT_FOUND_ROWS_RESOLVED_MUTATION,
  UPDATE_PIPELINE_FACILITY_ALTERNATE_IDS_MUTATION,
} from "./api";

interface FacilityMatchDialogProps {
  open: boolean;
  onClose: () => void;
  extractedAddress: ExtractedAddress | null;
  tenantId: string;
  pipelineId: string;
  rejectionReason: string;
}

type FacilityRow = FacilityMatchSearchQuery["facilities"]["items"][number];

// Pin the dialog body to a stable height so swapping between the search
// view and the edit view (and the brief loading spinner inside edit mode)
// does not resize the dialog paper. Roughly matches the search view's
// natural height (DataGrid 400px + cards/input + spacing).
const DIALOG_BODY_MIN_HEIGHT = 600;

function buildInitialDisplaySearch(address: ExtractedAddress | null): string {
  if (!address) return "";
  const parts: string[] = [];
  if (address.streetAddress) parts.push(address.streetAddress);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zip) parts.push(address.zip);
  return parts.join(" ");
}

export function FacilityMatchDialog({
  open,
  onClose,
  extractedAddress,
  tenantId,
  pipelineId,
  rejectionReason,
}: FacilityMatchDialogProps) {
  const initialDisplaySearch = buildInitialDisplaySearch(extractedAddress);
  const [searchInput, setSearchInput] = useState(initialDisplaySearch);
  const [debouncedSearch] = useDebounce(searchInput, 400);
  const [editingFacility, setEditingFacility] = useState<FacilityRow | null>(null);

  // Reset search input + edit state when dialog opens with new address data
  useEffect(() => {
    if (open) {
      setSearchInput(initialDisplaySearch);
      setEditingFacility(null);
    }
  }, [open, initialDisplaySearch]);

  const fullSearch = `tenantId:${tenantId} ${debouncedSearch}`.trim();

  const { data, loading } = useQuery(FACILITY_SEARCH_QUERY, {
    variables: { search: fullSearch, page: 0, pageSize: 25 },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const facilities = data?.facilities?.items ?? [];

  const columns: GridColDef<FacilityRow>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Facility Name",
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<FacilityRow>) => (
          <Box
            component={Link}
            to={`/o/${tenantId}/facilities/${params.row.id}/profile`}
            target="_blank"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.row.name}
          </Box>
        ),
      },
      { field: "streetAddress1", headerName: "Street Address", width: 200 },
      { field: "city", headerName: "City", width: 140 },
      { field: "state", headerName: "State", width: 80 },
      { field: "zip", headerName: "Zip", width: 100 },
      {
        field: "customerFacilityId",
        headerName: "Customer ID",
        width: 140,
      },
      {
        field: "_actions",
        headerName: "",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<FacilityRow>) => (
          <Tooltip title="Update facility alternate IDs">
            <IconButton size="small" color="primary" onClick={() => setEditingFacility(params.row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [tenantId]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      title={editingFacility ? "Update Facility Alternate IDs" : "Find Matching Facility"}
      noPrimaryAction
      hideCancelButton
    >
      {editingFacility ? (
        <Stack spacing={2} sx={{ pb: 1, minHeight: DIALOG_BODY_MIN_HEIGHT }}>
          <Box>
            <Button size="small" startIcon={<ArrowBack />} onClick={() => setEditingFacility(null)}>
              Back to search
            </Button>
          </Box>
          <RejectedRowAddressCard extractedAddress={extractedAddress} />
          <Card variant="outlined">
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Facility
              </Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <InfoChip label="Name" value={editingFacility.name ?? "—"} />
                {editingFacility.streetAddress1 && (
                  <InfoChip label="Street" value={editingFacility.streetAddress1} />
                )}
                {editingFacility.city && <InfoChip label="City" value={editingFacility.city} />}
                {editingFacility.state && <InfoChip label="State" value={editingFacility.state} />}
                {editingFacility.zip && <InfoChip label="Zip" value={editingFacility.zip} />}
                {editingFacility.customerFacilityId && (
                  <InfoChip label="Customer ID" value={editingFacility.customerFacilityId} />
                )}
              </Stack>
            </CardContent>
          </Card>
          <EditFacilityAlternateIdsForm
            facility={editingFacility}
            pipelineId={pipelineId}
            rejectionReason={rejectionReason}
            onSaved={() => {
              setEditingFacility(null);
              onClose();
            }}
            onCancel={() => setEditingFacility(null)}
          />
        </Stack>
      ) : (
        <Stack spacing={3} sx={{ minHeight: DIALOG_BODY_MIN_HEIGHT }}>
          <RejectedRowAddressCard
            extractedAddress={extractedAddress}
            onValueClick={(value) => setSearchInput(value)}
          />

          {/* Search input */}
          <TextField
            fullWidth
            size="small"
            label="Search facilities"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {loading ? <CircularProgress size={20} /> : <Search color="action" />}
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Results grid */}
          <Box sx={{ height: 400 }}>
            <DataGrid
              rows={facilities}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              pageSizeOptions={[25]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              disableRowSelectionOnClick
              noRowsMessage="No facilities found. Try adjusting the search."
            />
          </Box>

          {data?.facilities && data.facilities.count > facilities.length && (
            <Typography variant="caption" color="text.secondary">
              Showing {facilities.length} of {data.facilities.count.toLocaleString()} results
            </Typography>
          )}
        </Stack>
      )}
    </Dialog>
  );
}

function RejectedRowAddressCard({
  extractedAddress,
  onValueClick,
}: {
  extractedAddress: ExtractedAddress | null;
  onValueClick?: (value: string) => void;
}) {
  const alerts = useAlerts();
  const handleClick = (raw: string) => {
    if (onValueClick) {
      onValueClick(raw);
      return;
    }
    void navigator.clipboard
      .writeText(raw)
      .then(() => alerts.success(`Copied "${raw}"`))
      .catch(() => alerts.error("Could not copy to clipboard"));
  };
  const epaId = extractedAddress?.epaId ?? null;
  const vendorCode = extractedAddress?.vendorCode ?? null;
  const epaSearchValue = epaId ? `epaId:${epaId}` : null;

  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          Rejected Row Address Data
        </Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          {extractedAddress?.name && (
            <InfoChip
              label="Name"
              value={extractedAddress.name}
              onClick={() => handleClick(extractedAddress.name ?? "")}
            />
          )}
          {extractedAddress?.streetAddress && (
            <InfoChip
              label="Street"
              value={extractedAddress.streetAddress}
              onClick={() => handleClick(extractedAddress.streetAddress ?? "")}
            />
          )}
          {extractedAddress?.city && (
            <InfoChip
              label="City"
              value={extractedAddress.city}
              onClick={() => handleClick(extractedAddress.city ?? "")}
            />
          )}
          {extractedAddress?.state && (
            <InfoChip
              label="State"
              value={extractedAddress.state}
              onClick={() => handleClick(extractedAddress.state ?? "")}
            />
          )}
          {extractedAddress?.zip && (
            <InfoChip
              label="Zip"
              value={extractedAddress.zip}
              onClick={() => handleClick(extractedAddress.zip ?? "")}
            />
          )}
          <InfoChip
            label="EPA ID"
            value={epaId ?? "Not provided"}
            missing={!epaId}
            onClick={
              epaId
                ? () =>
                    onValueClick && epaSearchValue
                      ? onValueClick(epaSearchValue)
                      : handleClick(epaId)
                : undefined
            }
          />
          <InfoChip
            label="Vendor Code"
            value={vendorCode ?? "Not provided"}
            missing={!vendorCode}
            onClick={vendorCode && !onValueClick ? () => handleClick(vendorCode) : undefined}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function InfoChip({
  label,
  value,
  onClick,
  missing,
}: {
  label: string;
  value: string;
  onClick?: () => void;
  missing?: boolean;
}) {
  return (
    <Typography variant="body2">
      <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        {label}:
      </Typography>
      {onClick ? (
        <Box
          component="button"
          type="button"
          onClick={onClick}
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
            color: "primary.main",
            cursor: "pointer",
            fontWeight: "bold",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      ) : missing ? (
        <Box component="span" sx={{ color: "warning.main", fontStyle: "italic", fontWeight: 500 }}>
          {value}
        </Box>
      ) : (
        <strong>{value}</strong>
      )}
    </Typography>
  );
}

function EditFacilityAlternateIdsForm({
  facility,
  pipelineId,
  rejectionReason,
  onSaved,
  onCancel,
}: {
  facility: FacilityRow;
  pipelineId: string;
  rejectionReason: string;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const alerts = useAlerts();
  const { data, loading } = useQuery(FACILITY_ALTERNATE_IDS_QUERY, {
    variables: { id: facility.id },
    fetchPolicy: "cache-and-network",
  });
  const [updateAlternateIds, { loading: saving }] = useMutation(
    UPDATE_PIPELINE_FACILITY_ALTERNATE_IDS_MUTATION
  );
  const [markRowsResolved] = useMutation(MARK_FACILITY_NOT_FOUND_ROWS_RESOLVED_MUTATION);

  if (loading || !data?.facility) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, minHeight: 240 }}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <EditFacilityAlternateIdsFormInner
      isEpaIdLocked={data.facility.isEpaIdLocked ?? false}
      initialAlternateIds={data.facility.facilityAlternateIds ?? []}
      saving={saving}
      onSubmit={async (values) => {
        try {
          await updateAlternateIds({
            variables: {
              id: facility.id,
              facility: {
                facilityAlternateIds: values.facilityAlternateIds.map((alt) => ({
                  id: alt.id,
                  type: alt.type ?? "",
                  value: alt.value ?? "",
                  hasNoId: alt.hasNoId ?? false,
                  expiresAt: alt.expiresAt ?? null,
                })),
              },
            },
          });
          const markResult = await markRowsResolved({
            variables: { pipelineId, rejectionReason, resolvedFacilityId: facility.id },
            refetchQueries: ["WastePipelineRunRejectedRowGroups", "WastePipelineRunMetadata"],
            awaitRefetchQueries: true,
          });
          const count = markResult.data?.markFacilityNotFoundRowsResolved ?? 0;
          alerts.success(
            count > 0
              ? `Facility alternate IDs updated. ${count} rejected row${count === 1 ? "" : "s"} marked for reprocess.`
              : "Facility alternate IDs updated."
          );
          onSaved();
        } catch (err) {
          alerts.error(
            `Failed to update alternate IDs: ${err instanceof Error ? err.message : String(err)}`
          );
        }
      }}
      onCancel={onCancel}
    />
  );
}

type AlternateIdRow = {
  id: string;
  type?: string | null;
  value?: string | null;
  hasNoId?: boolean | null;
  expiresAt?: string | null;
};

function EditFacilityAlternateIdsFormInner({
  isEpaIdLocked,
  initialAlternateIds,
  saving,
  onSubmit,
  onCancel,
}: {
  isEpaIdLocked: boolean;
  initialAlternateIds: readonly AlternateIdRow[];
  saving: boolean;
  onSubmit: (values: FormShapeWithAlternateIds) => Promise<void>;
  onCancel: () => void;
}) {
  const form = useForm<FormShapeWithAlternateIds>({
    defaultValues: {
      facilityAlternateIds: initialAlternateIds.map((alt) => ({
        id: alt.id,
        type: alt.type ?? "",
        value: alt.value ?? "",
        hasNoId: alt.hasNoId ?? false,
        expiresAt: alt.expiresAt ?? null,
      })),
    },
  });

  const handleSubmit = form.handleSubmit(onSubmit);

  return (
    <FormProvider {...form}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <GovernmentIdFields isEpaIdLocked={isEpaIdLocked} />
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: "auto", pt: 2 }}>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </Box>
    </FormProvider>
  );
}
